'use client';

import { Toast, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { AlertCircle, Bell, CheckCircle2, Info, XCircle } from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info' | 'notification' | null | undefined;

const iconMap = {
  success: <CheckCircle2 className='h-5 w-5 shrink-0 text-white' />,
  error: <XCircle className='h-5 w-5 shrink-0 text-white' />,
  warning: <AlertCircle className='h-5 w-5 shrink-0 text-white' />,
  info: <Info className='h-5 w-5 shrink-0 text-white' />,
};

export function createToast({
  variant,
  description = '',
  title,
  imageUrl,
}: {
  variant: ToastVariant;
  description?: string;
  title: string;
  imageUrl?: string;
}) {
  const isNotification = variant === 'notification';

  if (isNotification) {
    return {
      variant: variant,
      title: (
        <div className='flex w-full flex-col'>
          {imageUrl && (
            <div className='w-full overflow-hidden'>
              <img
                src={imageUrl}
                alt=''
                className='h-32 w-full object-cover'
                loading='eager'
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}
          <div className='px-4 py-3 pr-10'>
            <div className='flex items-center gap-2.5'>
              <Bell className='h-4 w-4 shrink-0 text-zinc-400 dark:text-zinc-500' />
              <span className='text-sm font-semibold leading-tight text-zinc-900 dark:text-zinc-50'>{title}</span>
            </div>
            {description && (
              <p className='ml-[26px] mt-1 text-[13px] leading-snug text-zinc-500 dark:text-zinc-400'>{description}</p>
            )}
          </div>
        </div>
      ),
      description: undefined,
      className:
        'top-0 right-0 flex fixed md:max-w-[380px] md:top-4 md:right-4 [&>[toast-close]]:text-zinc-400 [&>[toast-close]]:hover:text-zinc-600 [&>[toast-close]]:hover:bg-zinc-100 [&>[toast-close]]:dark:hover:text-zinc-300 [&>[toast-close]]:dark:hover:bg-zinc-800',
    };
  }

  const icon = variant ? iconMap[variant as keyof typeof iconMap] : null;

  return {
    variant: variant,
    description: description ? <p className='ml-7 text-sm opacity-90'>{description}</p> : undefined,
    title: (
      <div className='flex items-center gap-2'>
        {icon}
        <span className='font-medium'>{title}</span>
      </div>
    ),
    className: 'top-0 right-0 flex fixed md:max-w-[420px] md:top-4 md:right-4',
  };
}

export function Toaster() {
  const { toasts } = useToast();

  return (
    <ToastProvider swipeDirection='right'>
      {toasts.map(function ({ id, title, description, action, ...props }) {
        return (
          <Toast key={id} {...props}>
            <div className='grid gap-1'>
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && <ToastDescription>{description}</ToastDescription>}
            </div>
            {action}
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
